import { call, put, takeLatest } from "redux-saga/effects";
import {
  FETCH_ORDERS_REQUEST,
  fetchOrdersSuccess,
  fetchOrdersFailure,
  updateOrderStatusSuccess,
  updateOrderStatusFailure,
  UPDATE_ORDER_STATUS_REQUEST,
} from "./actions";
import { db } from "helpers/firebase/firebaseConfig";
import { collection, query, where, getDocs,getDoc, updateDoc, doc, addDoc } from "firebase/firestore";
import Cookies from "js-cookie";
import axios from 'axios';
import { toast } from "react-toastify";
import { createCancellationInvoice } from "helpers/api/cancellationHelper";

async function fetchOrdersFromAPI() {
  try {
    // Get the current provider ID from cookies
    const currentProviderId = Cookies.get("usersessionId", {
      secure: true,
      sameSite: "strict",
      path: "/",
    });

    if (!currentProviderId) {
      console.error("No provider ID found in cookies");
      return [];
    }

    // 1. Fetch ONLY orders for the current provider
    const ordersQuery = query(
      collection(db, "Booking"),
      where("service_providers_id", "==", currentProviderId) // Add this filter
    );
    const ordersSnapshot = await getDocs(ordersQuery); // Now only fetches relevant bookings
    const orders = [];
    
    // [Rest of your existing code remains exactly the same...]
    // 2. Collect all unique reference IDs
    const providerIds = new Set();
    const userIds = new Set();
    const serviceIds = new Set();
    const addressIds = new Set();
    
    ordersSnapshot.forEach(doc => {
      const data = doc.data();
      if (data.service_providers_id) providerIds.add(data.service_providers_id);
      if (data.app_user_id) userIds.add(data.app_user_id);
      if (data.serviceProviderServices) serviceIds.add(data.serviceProviderServices);
      if (data.user_address_id) addressIds.add(data.user_address_id);
    });

// 3. Fetch all related data in parallel with proper parentheses
const [providersSnapshot, usersSnapshot, servicesSnapshot, addressSnapshot] = await Promise.all([
  providerIds.size > 0 
    ? getDocs(
        query(
          collection(db, "service_providers"), 
          where("__name__", "in", Array.from(providerIds))
        )
      ).catch(() => ({ docs: [] }))
    : Promise.resolve({ docs: [] }),
  
  userIds.size > 0 
    ? getDocs(
        query(
          collection(db, "app_users"), 
          where("__name__", "in", Array.from(userIds))
        )
      ).catch(() => ({ docs: [] })) 
    : Promise.resolve({ docs: [] }),
  
  serviceIds.size > 0 
    ? getDocs(
        query(
          collection(db, "service_provider_services"), 
          where("__name__", "in", Array.from(serviceIds))
        )
      ).catch(() => ({ docs: [] })) 
    : Promise.resolve({ docs: [] }),

  addressIds.size > 0 
    ? getDocs(
        query(
          collection(db, "user_address"),
          where("__name__", "in", Array.from(addressIds))
        )
      ).catch(() => ({ docs: [] })) 
    : Promise.resolve({ docs: [] })
]);

    // 4. Create lookup maps (unchanged)
    const providersMap = new Map();
    providersSnapshot.docs.forEach(doc => {
      if (doc.exists()) {
        providersMap.set(doc.id, doc.data());
      }
    });

    const usersMap = new Map();
    usersSnapshot.docs.forEach(doc => {
      if (doc.exists()) {
        usersMap.set(doc.id, doc.data());
      }
    });

    const servicesMap = new Map();
    servicesSnapshot.docs.forEach(doc => {
      if (doc.exists()) {
        servicesMap.set(doc.id, doc.data());
      }
    });

    const addressMap = new Map();
    addressSnapshot.docs.forEach(doc => {
      if (doc.exists()) {
        addressMap.set(doc.id, doc.data());
      }
    });

    // 5. Build orders (unchanged, but now only processes provider's orders)
    ordersSnapshot.forEach(doc => {
      const orderData = doc.data();
      
      const hasValidProvider = orderData.service_providers_id && providersMap.has(orderData.service_providers_id);
      const hasValidUser = orderData.app_user_id && usersMap.has(orderData.app_user_id);
      const hasValidService = orderData.serviceProviderServices && servicesMap.has(orderData.serviceProviderServices);

      if (hasValidProvider && hasValidUser && hasValidService) {
        orders.push({
          id: doc.id,
          ...orderData,
          service_providers_id: providersMap.get(orderData.service_providers_id),
          app_user_id: usersMap.get(orderData.app_user_id),
          serviceProviderServices: servicesMap.get(orderData.serviceProviderServices),
          user_address_id: addressMap.get(orderData.user_address_id),
        });
      }
    });

    return orders;

  } catch (error) {
    console.error("Error fetching orders:", error);
    return [];
  }
}





async function sendFirebaseNotification(notificationData, deviceToken) {
  const API_URL = 'https://node-server-eta-coral.vercel.app/send-notification';
  const title = notificationData.title;
  const body = notificationData.content;

  try {
    // Send to external notification service if token exists
    if (deviceToken) {
      try {
        await axios.post(API_URL, {
          deviceToken,
          title,
          body
        }, {
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': process.env.REACT_APP_NODE_API_KEY
          }
        });
      } catch (apiError) {
        console.error('Notification API error:', apiError.message);
      }
    }

    // Save notification to Firestore
    const docRef = await addDoc(
      collection(db, "app_notifications"),
      {
        ...notificationData,
        createdAt: new Date().toISOString()
      }
    );

    return { id: docRef.id, ...notificationData };
  } catch (error) {
    console.error('Error creating notification:', error);
    return null;
  }
}



function* fetchOrders() {
  try {
    const orders = yield call(fetchOrdersFromAPI);
    yield put(fetchOrdersSuccess(orders));
  } catch (error) {
    console.error("Failed to fetch orders:", error);
    yield put(fetchOrdersFailure(error.message));
    toast.error("Failed to load orders");
  }
}

async function updateOrderStatusInFirebase(orderId, newStatus) {
  try {
    const orderRef = doc(db, "Booking", orderId);
    
    // 1. First get current order data to preserve relationships
    const currentOrder = await getDoc(orderRef);
    if (!currentOrder.exists()) {
      throw new Error("Order not found");
    }
    const orderData = currentOrder.data();

    // 2. Update the status
    await updateDoc(orderRef, {
      booking_status: newStatus,
      updatedAt: new Date().toISOString()
    });

    // 3. If status is being changed to "cancelled_by_provider", increment cancellation count and create cancellation invoice
    if (newStatus === "cancelled_by_provider" && orderData.service_providers_id) {
      const providerRef = doc(db, "service_providers", orderData.service_providers_id);
      const providerDoc = await getDoc(providerRef);
      
      if (providerDoc.exists()) {
        const currentCount = providerDoc.data().order_cancellation_count || 0;
        await updateDoc(providerRef, {
          order_cancellation_count: currentCount + 1,
          lastCancellationDate: new Date().toISOString()
        });

        // Fetch service details before creating invoice
        let serviceData = null;
        if (orderData.serviceProviderServices) {
          const serviceDoc = await getDoc(doc(db, "service_provider_services", orderData.serviceProviderServices));
          if (serviceDoc.exists()) {
            serviceData = serviceDoc.data();
          }
        }

        // Create cancellation invoice for tracking with complete order data
        try {
          const completeOrderData = {
            id: orderId,
            ...orderData,
            serviceProviderServices: orderData.serviceProviderServices,
            service_name: serviceData?.name || serviceData?.name,
            service_price: orderData?.total_after_discount || orderData?.total_after_discount || orderData.total || 0,
            booking_execute_date: orderData?.booking_execute_date || null,
            cancelled_by: "provider",
          };

          const cancellationResult = await createCancellationInvoice(
            completeOrderData,
            orderData.service_providers_id,
            "مقدم الخدمة قام بإلغاء الطلب"
          );
          
          if (cancellationResult.success) {
            // console.log("Cancellation invoice created:", cancellationResult);
          } else {
            console.error("Failed to create cancellation invoice:", cancellationResult.error);
          }
        } catch (invoiceError) {
          console.error("Error creating cancellation invoice:", invoiceError);
        }


         // 3b. Only create refund request if order was paid - mark invoice for refund
        try {
          // Check if order payment status is "paid"
          const isPaid = orderData.paymentStatus === "paid";
          // console.log("💳 Order payment status:", orderData.paymentStatus, "- Is Paid:", isPaid);

          if (isPaid) {
            const invoiceQuery = query(
              collection(db, "service_provider_sale_invoices"),
              where("booking", "==", orderId)
            );
            const invoiceSnapshot = await getDocs(invoiceQuery);

            // console.log("📄 Invoice found:", !invoiceSnapshot.empty, "Count:", invoiceSnapshot.size);

            if (!invoiceSnapshot.empty) {
              const invoiceDoc = invoiceSnapshot.docs[0];
              const invoiceData = invoiceDoc.data();
              const invoiceId = invoiceDoc.id;

              // console.log("📝 Marking invoice as refund pending:", invoiceId);

              // Mark invoice as refund pending (for super admin dashboard to process)
              await updateDoc(doc(db, "service_provider_sale_invoices", invoiceId), {
                refund_status: "pending",
                refund_initiated_at: new Date().toISOString(),
                refund_reason: " تم إلغاء الطلب من قبل مقدم الخدمة",
                cancelled_order_id: orderId,
                needs_refund: true,
              });

              // console.log("✅ Invoice marked as refund pending - Super admin can process from dashboard");
            } else {
              console.warn("⚠️ No invoice found for paid booking:", orderId);
            }
          } else {
            // console.log("ℹ️ Order not paid - no refund needed. Payment status:", orderData.payment_status);
          }
        } catch (refundError) {
          console.error("❌ Error marking invoice for refund:", refundError);
          console.error("Error details:", refundError.message);
        }
      }
    }

    // 4. Fetch all related data in parallel
    const [serviceDoc, userDoc] = await Promise.all([
      orderData.serviceProviderServices ? 
        getDoc(doc(db, "service_provider_services", orderData.serviceProviderServices)) : 
        Promise.resolve(null),
      orderData.app_user_id ? 
        getDoc(doc(db, "app_users", orderData.app_user_id)) : 
        Promise.resolve(null)
    ]);

    // 5. Prepare enriched response
    return {
      id: orderId,
      ...orderData,
      booking_status: newStatus,
      updatedAt: new Date().toISOString(),
      serviceProviderServices: serviceDoc?.exists() ? {
        id: serviceDoc.id,
        ...serviceDoc.data()
      } : null,
      app_user_id: userDoc?.exists() ? {
        id: userDoc.id,
        ...userDoc.data()
      } : null
    };

  } catch (error) {
    console.error("Error updating order status:", error);
    throw error;
  }
}

function* updateOrderStatus({ payload }) {
  try {
    const response = yield call(
      updateOrderStatusInFirebase,
      payload.orderId,
      payload.newStatus
    );

    if (response) {
      const getType = (type) => {
        switch (type) {
          case "approved":
            return "تم الموافقة على الطلب";
          case "pending":
            return "قيد الانتظار";
          case "progress":
            return "قيد التنفيذ";
          case "completed":
            return "مكتمل";
          case "cancelled":
            return "تم الإلغاء";
          case "cancelled_by_user":
            return "تم الإلغاء من قبل المستخدم";
          case "cancelled_by_provider":
            return "تم الإلغاء من قبل المزود";
          case "cancelled_by_admin":
            return "تم الإلغاء من قبل الإدارة";
          case "refunded":
            return "تم استرجاع المبلغ";
          default:
            return type;
        }
      };

      const serviceName = response.serviceProviderServices?.name || 'Unknown Service';
      const notificationData = {
        title: "تحديث حالة الحجز",
        content: `تم تحديث حالة الحجز ${serviceName} إلى ${getType(payload.newStatus)}`,
        notification_type: "BOOKING",
        orderId: response.id,
        isSeen: false,
        appUser: response.app_user_id?.id || '',
        createdAt: new Date().toISOString()
      };

      // Send notification if user exists and has token
      if (response.app_user_id?.notification_token) {
        yield call(sendFirebaseNotification, notificationData, response.app_user_id.notification_token);
      }

      // Also save to notifications collection
      yield call(addDoc, collection(db, "app_notifications"), notificationData);
    }

    yield put(updateOrderStatusSuccess(response));
    toast.success("تم تحديث حالة الطلب بنجاح");
  } catch (error) {
    console.error("Failed to update order status:", error);
    yield put(updateOrderStatusFailure(error.message));
    toast.error("فشل تحديث حالة الطلب: " + (error.message || "حدث خطأ"));
  }
}

function* watchFetchOrders() {
  yield takeLatest(FETCH_ORDERS_REQUEST, fetchOrders);
  yield takeLatest(UPDATE_ORDER_STATUS_REQUEST, updateOrderStatus);
}

export default watchFetchOrders;
